import React, { useCallback, useEffect } from 'react';
import _ from 'lodash';
import { t } from 'i18next';
import { Trans } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Button, Dropdown, Icon, Loading } from '@brighterion/br-ui';
import { handleOnClickEvent, handleKeyEvent } from 'Utils';
import { useNotificationsStore } from 'Hooks/zustand/useNotificationsStore';
import { useTemplateStore } from 'Hooks/zustand/useTemplateStore';
import useGetScheduledDeployment from 'Hooks/query-hooks/scheduled-deployment/useGetScheduledDeployment';
import useScheduledDeploymentSummary from 'Hooks/query-hooks/scheduled-deployment/useScheduledDeploymentSummary';
import useReduxModal from 'Hooks/reduxHelperHooks/useReduxModal';
import { EventNames, EventTypes, trackEvent } from '@brighterion/insights';
import ScheduledDeploymentModal from '../ScheduledDeploymentModal';

import styles from './ScheduledDeploymentButton.css';

type ScheduledDeploymentButtonProps = {
    rulesType?: RulesType;
};

const ScheduledDeploymentButton: React.FC<ScheduledDeploymentButtonProps> = ({ rulesType }) => {
    const { templateRepositoryId } = useTemplateStore.getState().template;
    const { operation } = useNotificationsStore.getState().scheduledDeployment;
    const history = useHistory();
    const [isOpen, setIsOpen] = React.useState(false);
    const [isFirstVisit, setIsFirstVisit] = React.useState(true);
    const { addModal, pop } = useReduxModal();
    const scheduledDeployment = useGetScheduledDeployment({ options: { polling: !isOpen } });
    const scheduledDeploymentSummary = useScheduledDeploymentSummary(templateRepositoryId);
    const setOperation = useNotificationsStore((state) => state.setOperation);

    const handleMouseEnter = () => {
        scheduledDeploymentSummary.refetch();
        scheduledDeployment.refetch();
    };

    const handleModal = () => {
        if (!scheduledDeployment.isRefetching || typeof scheduledDeployment.data?.status === 'undefined') {
            setOperation('create');
            setIsOpen(true);
        } else {
            setIsOpen(false);
        }
    };

    const handleCloseModal = (isSubmit?: boolean) => {
        if (!isSubmit) {
            // avoid to trigger the Cancel Event when submitting/scheduling a deployment
            switch (rulesType) {
                case 'globalRules': {
                    trackEvent({
                        eventType: EventTypes.track,
                        eventName: EventNames.click,
                        payload: {
                            clickIdentifier: 'Review Deployment - Cancel',
                            text: 'Cancel Global Rules Deployment'
                        }
                    });
                    break;
                }
                case 'merchantOverrides': {
                    trackEvent({
                        eventType: EventTypes.track,
                        eventName: EventNames.click,
                        payload: {
                            clickIdentifier: 'Review Deployment - Cancel',
                            text: 'Cancel Subject Deployment'
                        }
                    });
                    break;
                }
                default: {
                    break;
                }
            }
        }
        setIsOpen(false);
    };

    const scheduledDeploymentOptions = [
        {
            label: t('Edit'),
            iconName: 'pencil-1',
            handleClick: () => {
                setOperation('update');
                setIsOpen(true);
            }
        },
        {
            label: t('Cancel'),
            iconName: 'remove-1',
            handleClick: () => {
                setOperation('delete');
                setIsOpen(true);
            }
        }
    ];

    const showFailedDeploymentModal = useCallback(
        (revisionId?: number) => {
            addModal({
                id: 'scheduled-deploy-error',
                status: 'error',
                title: t('Deployment failed'),
                content: <Trans i18nKey='scheduledDeploymentFailedMessage' values={{ revisionId }} />,
                onActionText: 'View Revisions',
                onAction: () => {
                    pop('scheduled-deploy-error');
                    history.push('/revisions');
                },
                cancelText: t('Close')
            });
        },
        [addModal, history, pop]
    );

    const handleFailedDeployment = () => {
        showFailedDeploymentModal(scheduledDeployment.data?.version);
    };

    useEffect(() => {
        if (scheduledDeployment.data?.status === 'failed' && isFirstVisit) {
            setIsFirstVisit(false);
            showFailedDeploymentModal(scheduledDeployment.data?.version);
        }
    }, [scheduledDeployment.data, isFirstVisit, showFailedDeploymentModal]);

    if (scheduledDeployment.isLoading || scheduledDeployment.data?.status === 'done') {
        return (
            <Button dataTestId='scheduled-deployment-btn' variant='primary' size='small'>
                <Loading size='sm' />
            </Button>
        );
    }

    if (scheduledDeployment.error?.statusText === 'offline') {
        return (
            <Button dataTestId='scheduled-deployment-btn' variant='primary' size='small' disabled>
                {t('Deployment Offline')}
            </Button>
        );
    }

    if (scheduledDeployment.data?.status === 'in_progress') {
        return (
            <Button dataTestId='scheduled-deployment-btn' variant='primary' size='small' disabled>
                {t('Deployment In Progress')}
            </Button>
        );
    }

    if (scheduledDeployment.data?.status === 'failed') {
        return (
            <Button
                dataTestId='scheduled-deployment-btn'
                variant='primary'
                size='small'
                onClick={handleOnClickEvent(handleFailedDeployment)}
                onKeyDown={handleKeyEvent(handleFailedDeployment)}
            >
                {t('Deploy')}
            </Button>
        );
    }

    return (
        <>
            {!scheduledDeployment.data ? (
                <Button
                    dataTestId='scheduled-deployment-btn'
                    variant='primary'
                    size='small'
                    onClick={handleOnClickEvent(handleModal)}
                    onKeyDown={handleKeyEvent(handleModal)}
                    onMouseEnter={handleMouseEnter}
                >
                    {t('Deploy')}
                </Button>
            ) : (
                <Dropdown
                    id='deployment-scheduled'
                    menuAlignment='right'
                    arrowSize='sm'
                    showArrows
                    closeOnClick
                    containerClassName={styles.primaryButton}
                    menuDropdownClassName={styles.dropDown}
                    aria-label='dropdown-scheduled'
                >
                    {t('Deployment Scheduled')}
                    <>
                        {scheduledDeploymentOptions.map((option) => {
                            const { iconName, handleClick, label } = option;
                            return (
                                <button
                                    type='button'
                                    key={_.uniqueId()}
                                    onClick={handleOnClickEvent(handleClick)}
                                    onKeyDown={handleKeyEvent(handleClick)}
                                    className={styles.dropdownOption}
                                >
                                    <Icon name={iconName} className={styles.icon} size='md' />
                                    <span className={styles.label}>{label}</span>
                                </button>
                            );
                        })}
                    </>
                </Dropdown>
            )}
            {isOpen && (
                <ScheduledDeploymentModal
                    modal={{
                        id: 'scheduled-deployment-modal',
                        name: 'scheduled-deployment',
                        title: t('Review Deployment'),
                        operation
                    }}
                    options={{ isOpen }}
                    closeModal={handleCloseModal}
                    status='warning'
                    rulesType={rulesType}
                />
            )}
        </>
    );
};

export default ScheduledDeploymentButton;
